import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import {
  type SuiCommandItem,
  type SuiBreadcrumbItem,
  type FilterDefinition,
  PlatformKeyPipe,
  SuiBreadcrumbsComponent,
  SuiDropdownDirective,
  SuiFilterDropdownComponent,
  SuiSidebarService,
  SuiThemeMode,
  SuiThemeService,
  SuiTooltipDirective,
  SuiAvatarComponent,
  StopPropagationDirective,
} from '@semeris/ui';
import { environment } from 'projects/semeris/src/environments/environment';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { DocumentViewerSelectors } from 'projects/semeris/src/store/document-viewer/document-viewer.selectors';
import { activeSearchTool, searchToolLink } from 'projects/semeris/src/store/menu/menu.selectors';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { UserProfileActions } from 'projects/semeris/src/store/user-profile/user-profile.actions';
import { UserProfileSelectors } from 'projects/semeris/src/store/user-profile/user-profile.selectors';
import { take } from 'rxjs/operators';

import { DocumentViewerActions } from '../../../../store/document-viewer/document-viewer.actions';
import { type CmdItemData } from '../../../command-palette/models/command-palette.models';
import { CommandPaletteOrchestratorService } from '../../../command-palette/services/command-palette-orchestrator.service';
import { DealPortfolioMembershipService } from '../../../deal-filter/services/deal-portfolio-membership.service';
import { XRayOptions } from '../../../document-viewer/models/document-viewer.models';
import { FEEDBACK_TEMPLATE_MAP } from '../../../feedback/components/feedback-modal/feedback-modal.component';
import { HelpIconComponent } from '../../../help/components/help-icon/help-icon.component';
import { HELP_ID_DEFAULT, HelpComponentMode } from '../../../help/models/help.model';
import { helpPageLink } from '../../../help/utils/help-page-url';
import { workspaceColorValue } from '../../../home/models/workspace-color';
import { ThemePickerComponent } from '../../../shared/components/theme-picker/theme-picker.component';
import { SemerisRoutes } from '../../../shared/constants/routes';
import { HasAssetDirective } from '../../../shared/directives/has-asset.directive';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { TenantNameResolverPipe } from '../../../shared/pipes/tenant-name-resovler.pipe';
import { UserNameResolverPipe } from '../../../shared/pipes/user-name-resovler.pipe';
import { WsLinkPipe } from '../../../shared/pipes/ws-link.pipe';
import { BreadcrumbService } from '../../../shared/services/breadcrumb.service';
import { DeploymentSwitchService } from '../../../shared/services/deployment/deployment-switch.service';
import { type DeploymentVariant, VARIANT_LABELS } from '../../../shared/services/deployment/deployment-variant';
import { PermissionService } from '../../../shared/services/permission.service';
import { WebSocketConnectionState, WebSocketService } from '../../../shared/services/web-socket/web-socket.service';
import { createPanelToggleGuard } from '../../../shared/utils/panel-toggle-guard';
import { wsLink } from '../../../shared/utils/workspace-link';
import { ManagerApprovalDropdownComponent } from '../manager-approval-dropdown/manager-approval-dropdown.component';

type EnvBadgeColor = 'neutral' | 'info' | 'warning' | 'secondary';

/** Dot colour + copy per connection state. Copy names the consequence (stale data), not the mechanism. */
const CONNECTION_DOT: Record<WebSocketConnectionState, { dotClass: string; tooltip: string }> = {
  connecting: { dotClass: 'sui-dot-neutral', tooltip: 'Connecting…' },
  connected: { dotClass: 'sui-dot-primary', tooltip: 'Live updates on' },
  reconnecting: { dotClass: 'sui-dot-warning', tooltip: 'Live updates paused — reconnecting' },
  offline: { dotClass: 'sui-dot-error', tooltip: 'Offline — live updates paused' },
  unsupported: { dotClass: 'sui-dot-error', tooltip: 'Live updates unavailable in this browser' },
  limited: { dotClass: 'sui-dot-warning', tooltip: 'Live updates may be delayed' },
  error: { dotClass: 'sui-dot-error', tooltip: 'Live updates paused — data may be stale' },
};

interface EnvBadge {
  label: string;
  color: EnvBadgeColor;
  tooltip: string;
}

interface DeploymentOption {
  variant: DeploymentVariant;
  label: string;
  tooltip: string;
}

/** Per-environment badge metadata. Customer-facing prod envs (prod, produsb) are absent — no badge there. */
const ENV_BADGES: Record<string, Omit<EnvBadge, 'tooltip'>> = {
  local: { label: 'Local', color: 'neutral' },
  dev: { label: 'Dev', color: 'info' },
  staging: { label: 'Staging', color: 'warning' },
  test: { label: 'Test', color: 'secondary' },
};

function resolveEnvBadge(): EnvBadge | null {
  const meta = ENV_BADGES[environment.envId];
  if (!meta) {
    return null;
  }
  return { ...meta, tooltip: `Backend: ${environment.apiUrl}` };
}

interface DeploymentOption {
  variant: DeploymentVariant;
  label: string;
  tooltip: string;
}

const DEPLOYMENT_OPTIONS: readonly DeploymentOption[] = [
  { variant: 'classic', label: VARIANT_LABELS.classic, tooltip: 'The current interface' },
  { variant: 'next', label: VARIANT_LABELS.next, tooltip: 'The new interface — same data' },
];

const DEPLOYMENT_INERT_TOOLTIP = 'Switching is unavailable on localhost';

@Component({
  imports: [
    AsyncPipe,
    RouterModule,
    PlatformKeyPipe,
    SuiBreadcrumbsComponent,
    SuiDropdownDirective,
    SuiFilterDropdownComponent,
    SuiTooltipDirective,
    SuiAvatarComponent,
    StopPropagationDirective,
    ThemePickerComponent,
    ManagerApprovalDropdownComponent,
    HelpIconComponent,
    HasAssetDirective,
    PermissionsDirective,
    TenantNameResolverPipe,
    UserNameResolverPipe,
    WsLinkPipe,
  ],
  selector: 'semeris-toolbar',
  templateUrl: './toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarComponent {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(SuiThemeService);
  private readonly sidebarService = inject(SuiSidebarService);
  private readonly cmdPaletteOrchestrator = inject(CommandPaletteOrchestratorService);
  private readonly permissionService = inject(PermissionService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly deploymentSwitch = inject(DeploymentSwitchService);
  private readonly portfolioMembership = inject(DealPortfolioMembershipService);

  /* ── Command palette templates ─────────────────────────── */

  private readonly resultTpl = viewChild<TemplateRef<{ $implicit: SuiCommandItem; active: boolean }>>('resultTpl');
  private readonly actionsTpl = viewChild<TemplateRef<void>>('actionsTpl');

  constructor() {
    afterNextRender(() => {
      const resultTpl = this.resultTpl();
      const actionsTpl = this.actionsTpl();
      if (resultTpl && actionsTpl) {
        this.cmdPaletteOrchestrator.registerTemplates(resultTpl, actionsTpl);
      }
    });
  }

  public isInBasket(transactionId: string): boolean {
    return this.portfolioMembership.isInBasket(transactionId);
  }

  /** The palette stays open: adding several deals in one search is the point of the control. */
  public onToggleBasket(transactionId: string): void {
    this.portfolioMembership.setBasketMembership(transactionId, !this.isInBasket(transactionId));
  }

  public readonly Permissions = Permission;
  public readonly XRayOptions = XRayOptions;
  public readonly themeMode = this.themeService.mode;
  public readonly envBadge = resolveEnvBadge();

  /* ── Interface switch (classic ⇄ next) ─────────────────── */

  public readonly deploymentVariant = this.deploymentSwitch.variant;

  /** Rendered on localhost too, where it shows this build's side but cannot navigate. */
  public readonly showDeploymentToggle = this.deploymentSwitch.isPaired || this.deploymentSwitch.isLocal;

  public readonly deploymentVariants = this.deploymentSwitch.isPaired
    ? DEPLOYMENT_OPTIONS
    : DEPLOYMENT_OPTIONS.map((option) => ({ ...option, tooltip: DEPLOYMENT_INERT_TOOLTIP }));

  /** The option NOT currently active on a host that can switch — drives the fallback below `lg`. */
  public readonly otherDeploymentVariant = this.deploymentSwitch.isPaired
    ? (DEPLOYMENT_OPTIONS.find((option) => option.variant !== this.deploymentVariant) ?? null)
    : null;

  public onSwitchDeployment(variant: DeploymentVariant): void {
    this.deploymentSwitch.switchTo(variant);
  }

  /** Drives the Notes button's active state — the panel is a persistent region, not a transient overlay. */
  public readonly notesPanelOpen = this.store.selectSignal(AppSelectors.notesPanelOpen);

  private readonly canToggleNotesPanel = createPanelToggleGuard();

  /** Email-verified or SSO. The `?` icon is for signed-up users; `/help/**` itself is open to all. */
  public readonly verifiedUser = toSignal(this.permissionService.verified$, { initialValue: false });

  /* ── Help ──────────────────────────────────────────────── */

  public readonly userGuideLink = helpPageLink(HELP_ID_DEFAULT);

  public readonly canRunGuidedTours = computed(() => this.userPermissions().includes(Permission.READ_GUIDED_TOUR));

  /** Both entries can be hidden at once, and a lone `Help` heading would still render. */
  public readonly showHelpMenu = computed(() => this.verifiedUser() || this.canRunGuidedTours());

  /** Dashboard first, then the panel — the tours tab is a launcher, and its first tour starts there. */
  public onOpenGuidedTours(): void {
    // Not gated on the resolved boolean: a same-URL navigation resolves `false`, so checking it
    // meant the panel never opened when you were already on the dashboard.
    void this.router
      .navigate(wsLink(SemerisRoutes.home.root))
      .then(() => this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.GuidedTours })));
  }

  /** Drives the sidebar toggle's active state: expanded inline, or the float panel showing below `lg`. */
  public readonly sidebarOpen = computed(() => this.sidebarService.mode() === 'expanded' || this.sidebarService.slots().length > 0);

  private readonly workspaceEnabledTools = this.store.selectSignal(AppSelectors.selectedWorkspaceEnabledTools);

  /** Search screen the palette's full-text action opens; `null` hides it (no search in this workspace). */
  public readonly searchTool = computed(() => activeSearchTool(this.workspaceEnabledTools(), this.userPermissions()));

  /* ── Breadcrumbs ── */

  private readonly selectedWorkspace = this.store.selectSignal(AppSelectors.selectedWorkspace);

  /** False on routes with no `data.breadcrumb` annotation — the trail is hidden rather than faked. */
  public readonly hasBreadcrumbs = computed(() => this.breadcrumbService.items().length > 0);

  /** Root item mirrors the sidebar workspace selector: palette dot when one is active, coins icon when not. */
  private readonly workspaceRoot = computed<SuiBreadcrumbItem>(() => {
    const workspace = this.selectedWorkspace();
    const url = `/${SemerisRoutes.home.root}`;
    return workspace
      ? { label: workspace.displayName, url, color: workspaceColorValue(workspace.color) }
      : { label: 'Select workspace…', url, icon: 'coins' };
  });

  public readonly breadcrumbItems = computed<SuiBreadcrumbItem[]>(() => [this.workspaceRoot(), ...this.breadcrumbService.items()]);

  public onBreadcrumbClick(item: SuiBreadcrumbItem): void {
    if (item.url) {
      // `navigateByUrl`, not `navigate` — a crumb's url can carry a query string.
      void this.router.navigateByUrl(item.url);
    }
  }

  /* ── Signals from store ────────────────────────────────── */

  public readonly userInfo = toSignal(this.authService.user$);

  public readonly impersonatedTenant = this.store.selectSignal(UserProfileSelectors.impersonatedTenant);

  public readonly impersonatedUser = this.store.selectSignal(UserProfileSelectors.impersonatedUser);

  /** A user can be impersonated inside the own tenant, so either field alone means impersonating. */
  public readonly isImpersonating = computed(() => !!this.impersonatedTenant() || !!this.impersonatedUser());

  public readonly isReadOnlyImpersonation = this.store.selectSignal(UserProfileSelectors.isReadOnlyImpersonation);

  public readonly onboardingTenant = this.store.selectSignal(UserProfileSelectors.onboardingTenant);

  private readonly xRayOptions = this.store.selectSignal(DocumentViewerSelectors.xRayOptions);

  /* ── Real-time (IoT) connection status ─────────────────── */

  public readonly connectionDot = computed(() => CONNECTION_DOT[this.webSocketService.connectionState()]);

  /* ── X-Ray computed toggles ────────────────────────────── */

  public readonly xrayReflow = computed(() => this.xRayOptions().includes(XRayOptions.Reflow));
  public readonly xrayLegalLogic = computed(() => this.xRayOptions().includes(XRayOptions.LegalLogic));
  public readonly xrayEntities = computed(() => this.xRayOptions().includes(XRayOptions.Entities));
  public readonly hasActiveXray = computed(() => this.xRayOptions().length > 0);

  private readonly userPermissions = toSignal(this.permissionService.permissions$, { initialValue: [] });
  public readonly hasXrayPermission = computed(() => this.userPermissions().includes(Permission.READ_XRAY));

  /* ── X-Ray filter dropdown (lg–xl breakpoint) ─────────── */

  private static readonly XRAY_KEY_MAP: Record<string, XRayOptions> = {
    reflow: XRayOptions.Reflow,
    'legal-logic': XRayOptions.LegalLogic,
    entities: XRayOptions.Entities,
  };
  private static readonly XRAY_VAL_MAP = new Map(Object.entries(ToolbarComponent.XRAY_KEY_MAP).map(([k, v]) => [v, k]));

  public readonly xrayFilterDef: FilterDefinition = {
    key: 'xray',
    label: 'X-Ray',
    type: 'list',
    multiple: true,
    options: [
      { key: 'reflow', label: 'Reflow', icon: 'sui-icon-text-indent' },
      { key: 'legal-logic', label: 'Legal Logic', icon: 'sui-icon-git-branch' },
      { key: 'entities', label: 'Entities', icon: 'sui-icon-highlighter-circle' },
    ],
  };

  public readonly xrayFilterValue = signal<string[]>([]);

  /** Sync store → filter value */
  private readonly xrayFilterSync = effect(() => {
    const keys = this.xRayOptions()
      .map((o) => ToolbarComponent.XRAY_VAL_MAP.get(o) ?? '')
      .filter(Boolean);
    this.xrayFilterValue.set(keys);
  });

  /** Sync filter value → store on dropdown close */
  public onXrayFilterApply(): void {
    const options = this.xrayFilterValue()
      .map((k) => ToolbarComponent.XRAY_KEY_MAP[k])
      .filter(Boolean);
    this.store.dispatch(DocumentViewerActions.SetXRayOptions({ options, save: true }));
  }

  /* ── Command palette ────────────────────────────────────── */

  public openCommandPalette(): void {
    this.cmdPaletteOrchestrator.open();
  }

  public itemData(item: SuiCommandItem): CmdItemData | undefined {
    return item.data as CmdItemData | undefined;
  }

  /* ── Actions ───────────────────────────────────────────── */

  public toggleSidenav(): void {
    this.sidebarService.toggle();
  }

  public toggleXray(option: XRayOptions): void {
    const current = this.xRayOptions();
    const next = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
    this.store.dispatch(DocumentViewerActions.SetXRayOptions({ options: next, save: true }));
  }

  public onToggleNotesPanel(): void {
    if (!this.canToggleNotesPanel()) {
      return;
    }

    this.store.dispatch(AppActions.ToggleNotesPanel({}));
  }

  public onExitImpersonation(): void {
    this.store
      .select(UserProfileSelectors.profile)
      .pipe(take(1))
      .subscribe((profile) => {
        this.store.dispatch(
          UserProfileActions.UpdateProfile({
            profile: { ...profile, impersonatedTenant: null, impersonatedUser: null },
          }),
        );
      });
  }

  public setTheme(mode: SuiThemeMode): void {
    this.themeService.setMode(mode);
    this.store
      .select(UserProfileSelectors.profile)
      .pipe(take(1))
      .subscribe((profile) => {
        if (!profile) {
          return;
        }
        this.store.dispatch(UserProfileActions.UpdateProfile({ profile: { ...profile, theme: mode } }));
      });
  }

  public onFeedback(): void {
    this.store.dispatch(AppActions.OpenFeedback({ ...FEEDBACK_TEMPLATE_MAP.get('feedback') }));
  }

  public openFullTextSearch(): void {
    const tool = this.searchTool();
    if (!tool) {
      return;
    }

    this.cmdPaletteOrchestrator.close();
    void this.router.navigate(searchToolLink(tool));
  }

  public logout(): void {
    this.store.dispatch(AppActions.Logout());
  }
}
